/**
 * DataDive Pure Wishlist System
 * Full client-side wishlist with drawer, local storage persistence, and dynamic state syncing.
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'datadive_wishlist_items';

  const DDWishlist = {
    items: [],
    toastTimeout: null,

    init() {
      this.load();
      this.bindEvents();
      this.updateUI();
      window.DDWishlist = this;
    },

    load() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        this.items = stored ? JSON.parse(stored) : [];
        if (!Array.isArray(this.items)) this.items = [];

        // Auto-heal missing images for previously stored items
        this.items.forEach((item) => {
          if (!item.image || item.image === 'null' || item.image.includes('no-image') || item.image.trim() === '') {
            if (item.handle) {
              fetch(`/products/${item.handle}.js`)
                .then((res) => res.json())
                .then((p) => {
                  let resolvedImg = p.featured_image || (p.images && p.images[0]) || '';
                  if (resolvedImg) {
                    if (resolvedImg.startsWith('//')) resolvedImg = 'https:' + resolvedImg;
                    else if (!resolvedImg.startsWith('http')) resolvedImg = 'https://' + resolvedImg;
                    item.image = resolvedImg;
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
                    this.renderDrawer();
                  }
                })
                .catch(() => {});
            }
          }
        });
      } catch (e) {
        console.error('Error loading wishlist:', e);
        this.items = [];
      }
    },

    save() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
      } catch (e) {
        console.error('Error saving wishlist:', e);
      }
      this.updateUI();
      document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items: this.items } }));
    },

    getItems() {
      return this.items;
    },

    getCount() {
      return this.items.length;
    },

    has(id) {
      if (!id) return false;
      const strId = String(id).trim();
      return this.items.some((item) => String(item.id).trim() === strId || String(item.variant_id).trim() === strId);
    },

    add(productData, openDrawer = true) {
      if (!productData || !productData.id) return;
      const id = String(productData.id).trim();
      const initialQty = parseInt(productData.quantity, 10) > 0 ? parseInt(productData.quantity, 10) : 1;

      if (!this.has(id)) {
        let img = productData.image || productData.featured_image;
        if (!img && Array.isArray(productData.images) && productData.images.length > 0) {
          img = productData.images[0];
        }
        if (img && typeof img === 'string') {
          if (img.startsWith('//')) img = 'https:' + img;
          else if (!img.startsWith('http') && img.length > 5) img = 'https://' + img;
        } else {
          img = '';
        }

        const item = {
          id: id,
          variant_id: productData.variant_id ? String(productData.variant_id).trim() : id,
          handle: productData.handle || '',
          title: productData.title || 'Product',
          vendor: productData.vendor || 'DataDive',
          sku: productData.sku || '',
          price: productData.price || '',
          quantity: initialQty,
          image: img,
          url: productData.url || (productData.handle ? `/products/${productData.handle}` : '#'),
          added_at: new Date().toISOString()
        };
        this.items.unshift(item);
        this.save();
        this.showToast(item.title, 'added');
        if (openDrawer) {
          this.open();
        }
      } else {
        const existingItem = this.items.find(item => String(item.variant_id || item.id).trim() === id || (productData.variant_id && String(item.variant_id).trim() === String(productData.variant_id).trim()));
        if (existingItem) {
          existingItem.quantity = (parseInt(existingItem.quantity, 10) || 1) + initialQty;
          this.save();
          this.showToast(existingItem.title, 'added');
        } else {
          this.showToast(productData.title || 'Item', 'added');
        }
        if (openDrawer) {
          this.open();
        }
      }
    },

    updateQuantity(id, delta) {
      if (!id) return;
      const strId = String(id).trim();
      const item = this.items.find((it) => String(it.id).trim() === strId || String(it.variant_id).trim() === strId);
      if (item) {
        let currentQty = parseInt(item.quantity, 10) || 1;
        let newQty = currentQty + delta;
        if (newQty < 1) newQty = 1;
        item.quantity = newQty;
        this.save();
        this.renderDrawer();
      }
    },

    setQuantity(id, qty) {
      if (!id) return;
      const strId = String(id).trim();
      const item = this.items.find((it) => String(it.id).trim() === strId || String(it.variant_id).trim() === strId);
      if (item) {
        let newQty = parseInt(qty, 10);
        if (isNaN(newQty) || newQty < 1) newQty = 1;
        item.quantity = newQty;
        this.save();
        this.renderDrawer();
      }
    },

    remove(id) {
      if (!id) return;
      const strId = String(id).trim();
      const itemToRemove = this.items.find((item) => String(item.id).trim() === strId || String(item.variant_id).trim() === strId);
      const prevLength = this.items.length;
      this.items = this.items.filter((item) => String(item.id).trim() !== strId && String(item.variant_id).trim() !== strId);
      if (this.items.length !== prevLength) {
        this.save();
        if (itemToRemove) {
          this.showToast(itemToRemove.title || 'Item', 'removed');
        }
      }
    },

    toggle(productData, openDrawer = true) {
      if (!productData || !productData.id) return;
      const targetId = productData.variant_id ? String(productData.variant_id).trim() : String(productData.id).trim();
      const mainId = String(productData.id).trim();

      if (this.has(targetId) || this.has(mainId)) {
        this.remove(targetId);
        this.remove(mainId);
      } else {
        this.add(productData, openDrawer);
      }
    },

    clear() {
      this.items = [];
      this.save();
    },

    showClearConfirm() {
      const modal = document.getElementById('DDWishlistConfirmModal');
      if (modal) {
        modal.classList.add('is-active');
      }
    },

    hideClearConfirm() {
      const modal = document.getElementById('DDWishlistConfirmModal');
      if (modal) {
        modal.classList.remove('is-active');
      }
    },

    confirmClear() {
      this.hideClearConfirm();
      this.clear();
    },

    open() {
      const overlay = document.getElementById('DDWishlistOverlay');
      if (overlay) {
        this.renderDrawer();
        overlay.classList.add('is-active');
        document.body.style.overflow = 'hidden';
      }
    },

    close() {
      this.hideClearConfirm();
      const overlay = document.getElementById('DDWishlistOverlay');
      if (overlay) {
        overlay.classList.remove('is-active');
        const qsModal = document.getElementById('DDQuickShopModal');
        const rfqModal = document.getElementById('DDRfqModalBackdrop');
        const isAnyModalOpen = (qsModal && qsModal.classList.contains('is-open')) || (rfqModal && rfqModal.classList.contains('is-open'));
        if (!isAnyModalOpen) {
          document.body.style.overflow = '';
        }
      }
    },

    updateUI() {
      this.updateHeaderCount();
      this.updateButtons();
      this.renderDrawer();
    },

    updateHeaderCount() {
      const count = this.getCount();
      const countEl = document.getElementById('dd-wishlist-header-count');

      if (countEl) {
        const countSpan = countEl.querySelector('span[aria-hidden]');
        if (countSpan) countSpan.textContent = count;
        countEl.style.display = count > 0 ? 'flex' : 'none';
      }

      const cartBubble = document.querySelector('#cart-icon-bubble');
      if (cartBubble) {
        const bubbleBadge = cartBubble.querySelector('.cart-count-bubble');
        if (bubbleBadge) {
          const bSpan = bubbleBadge.querySelector('span[aria-hidden]');
          if (bSpan) bSpan.textContent = count;
          bubbleBadge.style.display = count > 0 ? 'flex' : 'none';
        }
      }

      const drawerCount = document.getElementById('DDWishlistDrawerCount');
      if (drawerCount) {
        drawerCount.textContent = count;
      }
    },

    updateButtons() {
      const bagSvg = '<svg class="dd-btn-icon icon-bag" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 6px; vertical-align: middle;"><rect x="4" y="6" width="16" height="15" rx="3"/><path d="M8 6V4a4 4 0 0 1 8 0v2"/></svg>';
      const checkSvg = '<svg class="dd-btn-icon icon-check" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 6px; vertical-align: middle;"><polyline points="20 6 9 17 4 12"/></svg>';

      // PDP submit buttons
      const pdpButtons = document.querySelectorAll('.dd-pdp-btn-atc, .product-form__submit, [id^="ProductSubmitButton-"]');
      pdpButtons.forEach((btn) => {
        const pdpForm = btn.closest('form') || btn.closest('.product-form');
        const dataScript = pdpForm ? pdpForm.querySelector('.dd-pdp-product-data') : document.querySelector('.dd-pdp-product-data');
        let pId = null;
        if (dataScript) {
          try {
            const data = JSON.parse(dataScript.textContent);
            pId = data.variant_id || data.id;
          } catch (e) {}
        }
        if (!pId) pId = btn.getAttribute('data-product-id');
        if (pId && this.has(pId)) {
          btn.classList.add('is-wishlist-saved', 'is-saved');
          btn.innerHTML = `${checkSvg}<span>Added</span>`;
        } else {
          btn.classList.remove('is-wishlist-saved', 'is-saved');
          btn.innerHTML = `${bagSvg}<span>Add to Bag</span>`;
        }
      });

      // Quick Shop button
      const qsAtcBtn = document.getElementById('DDQuickShopAtcBtn');
      if (qsAtcBtn && window.currentProductData) {
        const pId = window.currentProductData.id || window.currentProductData.variant_id;
        if (pId && this.has(pId)) {
          qsAtcBtn.classList.add('is-saved', 'is-wishlist-saved');
          qsAtcBtn.innerHTML = `${checkSvg}<span>Added</span>`;
        } else {
          qsAtcBtn.classList.remove('is-saved', 'is-wishlist-saved');
          qsAtcBtn.innerHTML = `${bagSvg}<span>Add to bag</span>`;
        }
      }

      // Card buttons
      const cardButtons = document.querySelectorAll('.dd-card-product .dd-btn-atc');
      cardButtons.forEach((btn) => {
        const card = btn.closest('.dd-card-product');
        const dataScript = card ? card.querySelector('.dd-quickshop-data') : null;
        if (dataScript) {
          try {
            const data = JSON.parse(dataScript.textContent);
            if (this.has(data.id) || this.has(data.variant_id)) {
              btn.classList.add('is-wishlist-saved', 'is-saved');
              btn.innerHTML = `${checkSvg}<span>ADDED</span>`;
            } else {
              btn.classList.remove('is-wishlist-saved', 'is-saved');
              btn.innerHTML = `${bagSvg}<span>ADD TO BAG</span>`;
            }
          } catch (e) {}
        }
      });
    },

    renderDrawer() {
      const container = document.getElementById('DDWishlistDrawerBody');
      const footer = document.getElementById('DDWishlistDrawerFooter');
      if (!container) return;

      if (this.items.length === 0) {
        container.innerHTML = `
          <div class="dd-wishlist-empty">
            <div class="dd-wishlist-empty-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="6" width="16" height="15" rx="3"/><path d="M8 6V4a4 4 0 0 1 8 0v2"/></svg>
            </div>
            <h3 class="dd-wishlist-empty-title">Your bag is empty</h3>
            <p class="dd-wishlist-empty-text">Explore our collection and add products to your bag to request a quote.</p>
            <a href="/collections/all" class="dd-wishlist-empty-btn" onclick="window.DDWishlist.close()">Explore Products</a>
          </div>
        `;
        if (footer) footer.style.display = 'none';
        return;
      }

      if (footer) footer.style.display = 'flex';

      let html = '<ul class="dd-wishlist-items">';
      this.items.forEach((item) => {
        let imgUrl = item.image;
        if (!imgUrl || imgUrl === 'null' || imgUrl.trim() === '') {
          imgUrl = '//cdn.shopify.com/s/assets/no-image-2048-5e487c9c761756cd7028405997e19207bd2e409da627f1139a57e1a704e1b97a_medium.gif';
        } else if (imgUrl.startsWith('//')) {
          imgUrl = 'https:' + imgUrl;
        }

        const safeTitle = (item.title || 'Product').replace(/"/g, '&quot;');
        const displayPrice = item.price && item.price !== 'QAR 0,00' && item.price !== '$0.00' && item.price !== '0' ? item.price : 'Quote on request';

        let imgInner = '';
        if (imgUrl && !imgUrl.includes('no-image')) {
          imgInner = `<img src="${imgUrl}" alt="${safeTitle}" loading="lazy" onerror="this.onerror=null;this.parentElement.innerHTML='<svg width=\\'22\\' height=\\'22\\' viewBox=\\'0 0 24 24\\' fill=\\'none\\' stroke=\\'#cbd5e1\\' stroke-width=\\'1.5\\'><rect x=\\'3\\' y=\\'3\\' width=\\'18\\' height=\\'18\\' rx=\\'2\\'/><circle cx=\\'8.5\\' cy=\\'8.5\\' r=\\'1.5\\'/><polyline points=\\'21 15 16 10 5 21\\'/></svg>';">`;
        } else {
          imgInner = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>`;
        }

        const qty = parseInt(item.quantity, 10) || 1;

        html += `
          <li class="dd-wishlist-item" data-id="${item.id}">
            <a href="${item.url}" class="dd-wishlist-item-thumb">
              ${imgInner}
            </a>
            <div class="dd-wishlist-item-details">
              <div class="dd-wishlist-item-top">
                <span class="dd-wishlist-item-vendor">${item.vendor || 'DataDive'}</span>
                <button type="button" class="dd-wishlist-item-remove" data-remove-id="${item.id}" aria-label="Remove item" title="Remove">
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                </button>
              </div>
              <a href="${item.url}" class="dd-wishlist-item-title">${item.title}</a>
              ${item.sku ? `<div class="dd-wishlist-item-sku">SKU: ${item.sku}</div>` : ''}
              <div class="dd-wishlist-item-bottom">
                <div class="dd-wishlist-qty-wrap">
                  <button type="button" class="dd-wishlist-qty-btn dd-wishlist-qty-minus" data-qty-change="-1" data-item-id="${item.id}" aria-label="Decrease quantity">
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                  </button>
                  <input type="number" class="dd-wishlist-qty-input" data-qty-input-id="${item.id}" value="${qty}" min="1" aria-label="Quantity" />
                  <button type="button" class="dd-wishlist-qty-btn dd-wishlist-qty-plus" data-qty-change="1" data-item-id="${item.id}" aria-label="Increase quantity">
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                  </button>
                </div>
                <div class="dd-wishlist-item-actions">
                  <button type="button" class="dd-wishlist-item-quote-btn" data-quote-id="${item.id}" aria-label="Request quote for ${safeTitle}" title="Request Quote">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
                    <span>Quote</span>
                  </button>
                  <a href="${item.url}" class="dd-wishlist-item-view-btn">
                    <span>View</span>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                  </a>
                </div>
              </div>
            </div>
          </li>
        `;
      });
      html += '</ul>';

      container.innerHTML = html;

      // Attach remove handlers
      const removeBtns = container.querySelectorAll('[data-remove-id]');
      removeBtns.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const rId = btn.getAttribute('data-remove-id');
          this.remove(rId);
        });
      });

      // Attach quantity stepper handlers
      const qtyBtns = container.querySelectorAll('[data-qty-change]');
      qtyBtns.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const itId = btn.getAttribute('data-item-id');
          const delta = parseInt(btn.getAttribute('data-qty-change'), 10) || 0;
          this.updateQuantity(itId, delta);
        });
      });

      const qtyInputs = container.querySelectorAll('[data-qty-input-id]');
      qtyInputs.forEach((input) => {
        input.addEventListener('change', (e) => {
          const itId = input.getAttribute('data-qty-input-id');
          this.setQuantity(itId, input.value);
        });
        input.addEventListener('click', (e) => {
          e.stopPropagation();
        });
      });

      // Attach individual quote handlers
      const quoteBtns = container.querySelectorAll('[data-quote-id]');
      quoteBtns.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const qId = btn.getAttribute('data-quote-id');
          this.quoteItem(qId);
        });
      });
    },

    showToast(title, action) {
      let toast = document.getElementById('DDWishlistToast');
      if (!toast) {
        toast = document.createElement('div');
        toast.id = 'DDWishlistToast';
        toast.className = 'dd-wishlist-toast';
        document.body.appendChild(toast);
      }

      const cleanTitle = (title || 'Item').length > 30 ? ((title || '').substring(0, 28) + '…') : title;
      let msg = '';
      let iconSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';

      if (action === 'added') {
        msg = `Added: <strong>${cleanTitle}</strong>`;
      } else if (action === 'removed') {
        iconSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#f87171" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
        msg = `Removed: <strong>${cleanTitle}</strong>`;
      } else {
        msg = `Already in bag: <strong>${cleanTitle}</strong>`;
      }

      toast.innerHTML = `
        <div class="dd-wishlist-toast-content">
          <span class="dd-wishlist-toast-icon">${iconSvg}</span>
          <span class="dd-wishlist-toast-text">${msg}</span>
        </div>
        <button type="button" class="dd-wishlist-toast-btn" onclick="window.DDWishlist.open()">View bag</button>
      `;

      toast.classList.add('is-visible');
      if (this.toastTimeout) clearTimeout(this.toastTimeout);
      this.toastTimeout = setTimeout(() => {
        toast.classList.remove('is-visible');
      }, 3500);
    },

    quoteItem(id) {
      if (!id) return;
      const strId = String(id).trim();
      const item = this.items.find((it) => String(it.id).trim() === strId || String(it.variant_id).trim() === strId);
      if (!item) return;
      const rawPhone = window.whatsappPhoneNumber || '97430632234';
      const phone = rawPhone.replace(/[^0-9]/g, '');
      const qty = parseInt(item.quantity, 10) || 1;
      const itemLink = item.url.startsWith('http') ? item.url : `${window.location.origin}${item.url}`;
      const msg = `Hello DataDive, I would like to request a quote for the following item from my bag:\n\n*${item.title}*\nQuantity: ${qty}\nSKU: ${item.sku || 'N/A'}\nLink: ${itemLink}`;
      const url = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
      window.open(url, '_blank');
    },

    quoteAll() {
      if (this.items.length === 0) return;
      const listText = this.items.map((it, idx) => `${idx + 1}. ${it.title} (Qty: ${it.quantity || 1}, SKU: ${it.sku || 'N/A'})`).join('\n');
      const rawPhone = window.whatsappPhoneNumber || '97430632234';
      const phone = rawPhone.replace(/[^0-9]/g, '');
      const msg = `Hello DataDive, I would like to request a quote for the items in my bag:\n\n${listText}`;
      const url = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
      window.open(url, '_blank');
    },

    bindEvents() {
      document.addEventListener('click', (e) => {
        const headerBtn = e.target.closest('#cart-icon-bubble, #wishlist-icon-bubble, .header__icon--cart, .header__icon--wishlist');
        if (headerBtn) {
          e.preventDefault();
          this.open();
        }

        const confirmModal = document.getElementById('DDWishlistConfirmModal');
        if (confirmModal && confirmModal.classList.contains('is-active')) {
          if (e.target === confirmModal) {
            this.hideClearConfirm();
            return;
          }
        }

        const closeBtn = e.target.closest('#DDWishlistClose, .dd-wishlist-overlay');
        if (closeBtn && (closeBtn.id === 'DDWishlistClose' || e.target === closeBtn)) {
          this.close();
        }

        const itemQuoteBtn = e.target.closest('[data-quote-id], .dd-wishlist-item-quote-btn');
        if (itemQuoteBtn) {
          e.preventDefault();
          e.stopPropagation();
          const qId = itemQuoteBtn.getAttribute('data-quote-id');
          this.quoteItem(qId);
          return;
        }

        const itemRemoveBtn = e.target.closest('[data-remove-id], .dd-wishlist-item-remove');
        if (itemRemoveBtn) {
          e.preventDefault();
          e.stopPropagation();
          const rId = itemRemoveBtn.getAttribute('data-remove-id');
          this.remove(rId);
          return;
        }

        const qtyBtn = e.target.closest('[data-qty-change], .dd-wishlist-qty-btn');
        if (qtyBtn) {
          e.preventDefault();
          e.stopPropagation();
          const itId = qtyBtn.getAttribute('data-item-id');
          const delta = parseInt(qtyBtn.getAttribute('data-qty-change'), 10) || 0;
          this.updateQuantity(itId, delta);
          return;
        }

        const cardAtcBtn = e.target.closest('.dd-card-product .dd-btn-atc');
        if (cardAtcBtn) {
          e.preventDefault();
          e.stopPropagation();
          const card = cardAtcBtn.closest('.dd-card-product');
          const dataScript = card ? card.querySelector('.dd-quickshop-data') : null;
          if (dataScript) {
            try {
              const data = JSON.parse(dataScript.textContent);
              this.toggle(data);
            } catch (err) {
              console.error(err);
            }
          }
        }

        // PDP Add to bag button
        const pdpBtn = e.target.closest('.product-form__submit, [id^="ProductSubmitButton-"], .dd-pdp-btn-atc');
        if (pdpBtn) {
          e.preventDefault();
          e.stopPropagation();
          const pdpForm = pdpBtn.closest('form') || pdpBtn.closest('.product-form');
          const dataScript = pdpForm ? pdpForm.querySelector('.dd-pdp-product-data') : document.querySelector('.dd-pdp-product-data');
          if (dataScript) {
            try {
              const data = JSON.parse(dataScript.textContent);
              const variantInput = pdpForm ? pdpForm.querySelector('[name="id"]') : null;
              if (variantInput && variantInput.value) {
                data.variant_id = variantInput.value;
              }
              const qtyInput = pdpForm ? pdpForm.querySelector('[name="quantity"], .quantity__input') : document.querySelector('[name="quantity"], .quantity__input');
              if (qtyInput && parseInt(qtyInput.value, 10) > 0) {
                data.quantity = parseInt(qtyInput.value, 10);
              }
              this.add(data, true);
            } catch (err) {
              console.error(err);
            }
          }
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => DDWishlist.init());
  } else {
    DDWishlist.init();
  }
})();
